import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link2, TriangleAlert } from 'lucide-react';
import { useCreateConnection } from 'queries/useConnection';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type AddConnectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function AddConnectionModal({ isOpen, onClose }: AddConnectionModalProps) {
  const [displayName, setDisplayName] = useState<string>('');
  const { t } = useTranslation();
  const [createError, setCreateError] = useState<string | null>(null);
  const { mutate: createConnection, isPending: isCreating } = useCreateConnection();
  const reset = () => {
    setDisplayName('');
    setCreateError(null);
  };
  const createNewConnection = async () => {
    if (!displayName?.trim()) {
      setCreateError(t('common.fieldRequired', { field: t('connection.name') }));
      return;
    }
    setCreateError(null);
    createConnection(
      { displayName: displayName.trim() },
      {
        onSuccess: (res) => {
          if (res.consent_link) {
            window.location.href = res.consent_link;
            onClose();
            reset();
          }
        },
      }
    );
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        reset();
      }}
    >
      <ModalOverlay bg='rgba(16,18,24,.45)' backdropFilter='blur(3px)' />
      <ModalContent maxW='520px' bg='bg.surfaceRaised' borderRadius='xl' shadow='ds_lg'>
        <ModalHeader
          color='text.default'
          fontFamily='heading'
          fontSize='lg'
          fontWeight={600}
          borderColor='border.default'
          borderBottomWidth='1px'
        >
          {t('connection.createConnection')}
        </ModalHeader>
        <ModalCloseButton color='text.muted' />
        <ModalBody py={6}>
          <VStack align='stretch' spacing={4}>
            <FormControl isInvalid={!!createError}>
              <FormLabel color='text.default' fontSize='sm' fontWeight={500}>
                {t('connection.name')}
              </FormLabel>
              <Input
                h='40px'
                color='text.default'
                bg='bg.surface'
                borderWidth='1px'
                borderColor={createError ? 'state.error' : 'border.strong'}
                borderRadius='md'
                _hover={{ borderColor: createError ? 'state.error' : 'border.strong' }}
                _focus={{
                  borderColor: createError ? 'state.error' : 'primary.solid',
                  boxShadow: createError
                    ? '0 0 0 3px var(--chakra-colors-state-errorSoft)'
                    : '0 0 0 3px var(--chakra-colors-primary-soft)',
                }}
                _placeholder={{ color: 'text.subtle' }}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  setCreateError(null);
                }}
                placeholder={t('connection.enterDisplayName')}
                value={displayName}
              />
              {!!createError && (
                <Text mt={1} color='state.error' fontSize='xs'>
                  {createError}
                </Text>
              )}
            </FormControl>

            {/* Microsoft consent notice — mockup warning callout */}
            <Flex
              align='flex-start'
              gap={3}
              p={3}
              bg='state.warningSoft'
              borderWidth='1px'
              borderColor='state.warning'
              borderRadius='md'
            >
              <TriangleAlert
                size={16}
                color='var(--chakra-colors-state-warning)'
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Text color='state.warning' fontSize='sm'>
                {t('connection.consentNotice', {
                  defaultValue:
                    "You'll be redirected to Microsoft to grant read access. Amatrium only requests the libraries you select.",
                })}
              </Text>
            </Flex>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3} borderColor='border.default' borderTopWidth='1px'>
          <Button
            onClick={() => {
              onClose();
              reset();
            }}
            size='ds-md'
            variant='ds-ghost'
          >
            {t('common.cancel')}
          </Button>
          <Button
            isLoading={isCreating}
            leftIcon={<Link2 size={16} />}
            onClick={createNewConnection}
            size='ds-md'
            variant='ds-primary'
          >
            {t('common.create')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddConnectionModal;
